/**
 * Booking Policy client — cấu hình luồng đặt lịch của phòng khám.
 * Real /api/v1/booking-policy (GET trả về effective policy đã resolve, không bao giờ 404).
 * Real-only (mirror services.ts / rooms.ts) — không dùng mock adapter.
 */
import { apiClient } from "./client";

/** Cách hiển thị một chiều (dimension) trong bước đặt lịch của bệnh nhân. */
export type DimensionMode = "HIDDEN" | "OPTIONAL" | "REQUIRED";

/** Cách điền bác sĩ / phòng khi bệnh nhân bỏ trống. */
export type FillMode = "AUTO" | "MANUAL" | "NONE";

/** Hình thức giờ hẹn. */
export type TimeMode = "SESSION" | "SLOT";

/** Chu kỳ reset số thứ tự (STT). */
export type QueueReset = "DAY" | "SESSION";

/** Phạm vi đánh số thứ tự (STT). */
/**
 * Phạm vi cấp STT. Chỉ còn hai giá trị (#19339) — "Theo khoa" (DEPARTMENT) và "Theo bác sĩ"
 * (DOCTOR) đã bị gỡ khỏi sản phẩm, máy chủ trả 400 nếu nhận chúng.
 */
export type QueueScope = "GENERAL" | "ROOM";

/**
 * Loại dịch vụ bệnh nhân TỰ ĐẶT được.
 *
 * EXAM_ONLY bỏ thủ thuật / xét nghiệm / chẩn đoán hình ảnh khỏi ô chọn: đó là những thứ bác sĩ chỉ
 * định trong lúc khám, và chúng không sinh phí khám để trả trước.
 */
export type ServiceScope = "ALL" | "EXAM_ONLY";

/** Bốn chiều đặt lịch, dùng làm khoá cấu hình thứ tự (dimOrder). */
export type BookingDimensionKey = "department" | "room" | "doctor" | "service";

export interface BookingPolicy {
  dimDepartment: DimensionMode;
  dimRoom: DimensionMode;
  dimDoctor: DimensionMode;
  dimService: DimensionMode;
  /**
   * Hình thức khám (BHYT/Dịch vụ) — #18673. Backend force-collapses this to HIDDEN when the
   * tenant's clinic_features.bhyt_enabled (Cài đặt → Phòng khám → Hình thức khám) is off.
   */
  dimPaymentType: DimensionMode;
  /**
   * Thứ tự hiển thị 4 chiều (Khoa/Phòng/Bác sĩ/Dịch vụ) — dùng chung cho màn đặt lịch
   * và màn khám. Optional: backend chưa cấu hình → FE dùng thứ tự mặc định
   * (department → room → doctor → service). Sẽ được ghi từ Phase 2 (migration + API).
   */
  dimOrder?: BookingDimensionKey[] | null;
  /**
   * Độc lập với dimService: chiều đó nói CÓ hiện bước "Dịch vụ" hay không, trường này nói bước đó
   * hiện NHỮNG GÌ. Dòng ghi trước khi có cột này đọc ra "ALL".
   */
  serviceScope: ServiceScope;
  doctorFill: FillMode;
  roomFill: FillMode;
  timeMode: TimeMode;
  slotMinutes: number;
  queueReset: QueueReset;
  queueScope: QueueScope;
}

export const DIMENSION_OPTIONS: { code: DimensionMode; label: string }[] = [
  { code: "HIDDEN", label: "Ẩn" },
  { code: "OPTIONAL", label: "Tùy chọn" },
  { code: "REQUIRED", label: "Bắt buộc" },
];

export const SERVICE_SCOPE_OPTIONS: { code: ServiceScope; label: string }[] = [
  { code: "ALL", label: "Tất cả dịch vụ" },
  { code: "EXAM_ONLY", label: "Chỉ dịch vụ khám bệnh" },
];

export const FILL_OPTIONS: { code: FillMode; label: string }[] = [
  { code: "AUTO", label: "Tự động" },
  { code: "MANUAL", label: "Lễ tân xếp" },
  { code: "NONE", label: "Không" },
];

export const TIME_MODE_OPTIONS: { code: TimeMode; label: string }[] = [
  { code: "SESSION", label: "Theo buổi" },
  { code: "SLOT", label: "Theo giờ" },
];

export const QUEUE_RESET_OPTIONS: { code: QueueReset; label: string }[] = [
  { code: "DAY", label: "Theo ngày" },
  { code: "SESSION", label: "Theo buổi" },
];

export const QUEUE_SCOPE_OPTIONS: { code: QueueScope; label: string }[] = [
  { code: "GENERAL", label: "Chung / Toàn chi nhánh" },
  { code: "ROOM", label: "Theo phòng" },
];

export function dimensionLabel(code: DimensionMode): string {
  return DIMENSION_OPTIONS.find((o) => o.code === code)?.label ?? code;
}

export const bookingPolicyApi = {
  /**
   * GET effective policy — never 404. No branch = tenant default; with a branch the server applies
   * that branch's override when it has one (#19665).
   */
  get(branchId?: string): Promise<BookingPolicy> {
    return apiClient
      .get<BookingPolicy>("/v1/booking-policy", { params: branchId ? { branchId } : undefined })
      .then((r) => r.data);
  },
  /** Upsert tenant default policy. */
  update(policy: BookingPolicy): Promise<BookingPolicy> {
    return apiClient
      .put<BookingPolicy>("/v1/booking-policy", policy)
      .then((r) => r.data);
  },
};

export const bookingPolicyQueryKeys = {
  all: () => ["booking-policy"] as const,
};
